import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Table from "../../core/pagination/datatable";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import CommonFooter from "../../core/common/footer/commonFooter";
import { 
  useDeleteCategoryMutation, 
  useGetCategoryListQuery,
} from "../../core/redux/api/productApi";
import AddCategoryModal from "./add-category-modal";
import toast from "react-hot-toast";

const CategoryList = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  const { data: dataSource, isLoading: categoryLoading, error: categoryError, refetch } = useGetCategoryListQuery();

  const [deleteCategory] = useDeleteCategoryMutation();

  const handleDelete = async () => {
    if (!categoryToDelete) return;
    
    try {
      await deleteCategory(categoryToDelete).unwrap();
      toast.success("Category deleted successfully!");
      refetch();
    } catch (error) {
      toast.error(`Unable to delete category: ${error?.message || "Something went wrong"}`);
    } finally {
      setShowDeleteModal(false);
      setCategoryToDelete(null);
    }
  };

  const columns = [
    {
      title: "Category Code",
      dataIndex: "categoryCode",
      sorter: (a, b) => a.categoryCode.localeCompare(b.categoryCode),
    },
    {
      title: "Category Image",
      dataIndex: "thumbnail",
      render: (thumbnail) => (
        <img
          src={thumbnail}
          alt="Category"
          style={{ width: 40, height: 40, borderRadius: '8px', objectFit: 'cover' }}
        />
      ),
    },
    {
      title: "Category Name",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Created On",
      dataIndex: "createdAt",
      render: (createdAt) => new Date(createdAt).toLocaleDateString(),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Actions",
      dataIndex: "_id",
      key: "actions",
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <Link
              className="me-2 p-2"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#add-category"
              onClick={() => {
                setSelectedCategory(record);
                setIsAddMode(false);     
              }}
            >
              <i data-feather="edit" className="feather-edit" />
            </Link>
            <Link
              className="p-2"
              to="#"
              onClick={(e) => { 
                e.preventDefault();
                setCategoryToDelete(record._id);
                setShowDeleteModal(true);
              }}
            >
              <i data-feather="trash-2" className="feather-trash-2" />
            </Link>
          </div>
        </div>
      ),
    },
  ];

  if (categoryLoading) return <div>Loading...</div>;
  if (categoryError) return <div>Error loading categories</div>;

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4 className="fw-bold">Category</h4>
                <h6>Manage your categories</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <TooltipIcons />
              <RefreshIcon onClick={refetch} />
              <CollapesIcon />
            </ul>
            <div className="page-btn">
              <Link
                to="#"
                className="btn btn-primary"
                data-bs-toggle="modal"
                data-bs-target="#add-category"
                onClick={() => {
                  setSelectedCategory(null);
                  setIsAddMode(true);     
                }}
              >
                <i className='ti ti-circle-plus me-1'></i>
                Add Category
              </Link>
            </div>
          </div>
          
          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set"></div>
              <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-2">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    Status
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-end p-3">
                    <li><Link to="#" className="dropdown-item rounded-1">Active</Link></li>
                    <li><Link to="#" className="dropdown-item rounded-1">Inactive</Link></li>
                  </ul>
                </div>
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    Sort By : Last 7 Days
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-end p-3">
                    <li><Link to="#" className="dropdown-item rounded-1">Recently Added</Link></li>
                    <li><Link to="#" className="dropdown-item rounded-1">Ascending</Link></li>
                    <li><Link to="#" className="dropdown-item rounded-1">Desending</Link></li>
                    <li><Link to="#" className="dropdown-item rounded-1">Last Month</Link></li>
                    <li><Link to="#" className="dropdown-item rounded-1">Last 7 Days</Link></li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive category-table">
                <Table columns={columns} dataSource={dataSource} />
              </div>
            </div>
          </div>
        </div>
        <CommonFooter />
      </div>

      <AddCategoryModal
        isEdit={!isAddMode}
        categoryData={selectedCategory}
    
        onSuccess={() => {
          setSelectedCategory(null);
          refetch();
        }}
      />

      {showDeleteModal && (
        <div className="modal fade show" id="delete-modal" style={{display: 'block'}}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content p-5">
              <div className="modal-body text-center p-0">
                <span className="rounded-circle d-inline-flex p-2 bg-danger-transparent mb-2">
                  <i className="ti ti-trash fs-24 text-danger" />
                </span>
                <h4 className="fs-20 text-gray-9 fw-bold mb-2 mt-1">
                  Delete Category
                </h4>
                <p className="text-gray-6 mb-0 fs-16">
                  Are you sure you want to delete this category?
                </p>
                <div className="d-flex justify-content-center mt-3">
                  <button
                    className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                    onClick={() => setShowDeleteModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary fs-13 fw-medium p-2 px-3"
                    onClick={handleDelete}
                  >
                    Yes Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    
    </div>
  );
};

export default CategoryList;